import React, { CSSProperties, Dispatch, LegacyRef, memo, MutableRefObject, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons } from "../../Styles";
import { LoadingSpinner, NewInput, NewSelect, UNIT } from "..";
import { CircleTypes } from "../ProgressViewer/_ProgressViewer/progressDefinitions";
import { LocaleFarmDate, NewFieldStyles, NewInputHandles, SelectFieldOption_Values } from "../../Definitions";
import { Graph_QL, MaintainAnimal, useTerminals } from "../../Services";
import { useAnimalConsumptionById } from "../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { Animal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import store, { useAppDispatch, useAppSelector } from "../../Store";
import { selectActiveSectionById } from "../../Store/ActiveStateHandlers/activeSectionSlice";
import { selectActiveAnimalById, updateActiveAnimal } from "../../Store/ActiveStateHandlers/activeAnimalSlice";
import { debounce, isEqual } from "lodash";
import { UnknownAction } from "redux";
//TODO
import { MemoProgressCircle } from "../ProgressViewer/progressCircle";
import NewLineInfoDisplay from "../Animals/SubComponents/newLineInfoDisplay";
import { selectFeedCurveOptionById, selectMilestoneOptionsByFeedCurve } from "../../Store/OptionsHandlers/optionsCombinedReducer";
import { UndefinedOptions, useFeedcurveOptions } from "../_Components";

type FeedSkipHandler = {
  feedSkipTime: { [key: number]: number };
  setFeedSkipTime: React.Dispatch<SetStateAction<{ [key: number]: number }>>;
  feedSkipSuccess: MutableRefObject<{ [locationId: number]: boolean }>;
}

type Fw_locationFeederProps = {
  dirtRef: MutableRefObject<MaintainAnimal[]>,
  locationId: number,
  editMode: boolean,
  feedSkipHandler: FeedSkipHandler,
}
export default function FvLocationFeeder({ locationId, editMode, dirtRef, feedSkipHandler }: Fw_locationFeederProps) {
  const { t } = useTranslation();

  // #region | Animal Info
  const activeSection = useAppSelector((state) => selectActiveSectionById(state, locationId));
  const animalInSection = activeSection?.animals.at(0);
  const animalAtLocation = useAppSelector((state) => selectActiveAnimalById(state, animalInSection?.animal_id ?? -1));
  // #endregion

  if (animalAtLocation === undefined) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", ...css.emptyPen, ...fonts.fontSize3, opacity: "0.5" }}>{t("locationFeederPages.sections.emptyPen", { defaultValue: "Empty Pen" })}</div>;

  // Stylingfv_lo
  const areas: CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: "0", height: "100%", boxSizing: "border-box" }

  return (<>
    {/* ---------- WHITE AREA ---------- */}
    <div style={{ gridArea: "1/1/2/2", backgroundColor: colors.greyLightTertiary, gap: "20px", ...areas }}>

      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px", height: "50px", flexShrink: "0", }}>
        <img src={icons.animalsColor} style={{ width: "50px" }} alt="icon" />
        <div>
          <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}> {t(`focus_viewPages.inThisPen`, { defaultValue: "in this pen" })} </p>
          <p style={{ ...fonts.header, ...fonts.fontSize2, color: colors.greyDarkPrimary }}>
            {`${t("animalPages.animal.animal", { defaultValue: "animal" })} ${animalAtLocation.animal_number}`}
          </p>
        </div>
      </div>

      {/* seperator */}
      <div style={{ width: "80%", minWidth: "280px", height: "2px", backgroundColor: colors.greyTertiary, flexShrink: "0" }} />
      <ProgressCircles animalId={animalAtLocation.animal_id} />
    </div>

    {/* ---------- GREY AREA ---------- */}
    <div style={{ gridArea: "2/1/3/2", backgroundColor: colors.greyPrimary, display: "flex", flexDirection: "column", gap: "10px", padding: "20px", ...areas }}>
      <FeedSkip key={`feedSkipComp_${locationId}`} locationId={locationId} editMode={editMode} feedSkipHandler={feedSkipHandler} />
      <FeedCorrection key={`feedCorrectionComp_${animalAtLocation.animal_id}`} dirtRef={dirtRef} animal={animalAtLocation} editMode={editMode} />
      <FeedCurveMilestoneDay key={`feedCurveMilestoneDayComp_${animalAtLocation.animal_id}`} dirtRef={dirtRef} animal={animalAtLocation} editMode={editMode} />
    </div>
  </>)
}

// #region | FeedCurve, Milestone and Milestone Day
const FeedCurveMilestoneDay = memo(({ dirtRef, animal, editMode = false }: { dirtRef: MutableRefObject<MaintainAnimal[]>, animal: Animal, editMode: boolean }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const inputRef = useRef<NewInputHandles>(null)

  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.nan)
  const milestoneOptionsFromFeedcurve = useAppSelector((state) => selectMilestoneOptionsByFeedCurve(state, [animal.feedcurve_id ?? -1]), isEqual);
  const selectedFeedCurveOption = useAppSelector((state) => selectFeedCurveOptionById(state, animal.feedcurve_id ?? -1))

  return (<>
    <NewLineInfoDisplay
      key={`feedCurve_${animal.animal_id}`}
      focusView={true}
      header={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })}
      enhancer={<glyphs.FeedCurve size="25px" />}
      content={feedCurveOptions.find(f => f.id === animal.feedcurve_id)?.label}
      noContent={(animal.feedcurve_id === undefined || animal.feedcurve_id === null) ? t("negativeParams.feedCurve", { defaultValue: "No Feed Curve" }) : undefined}
      editMode={editMode}
      editContent={
        <NewSelect
          isClearable
          options={feedCurveOptions ?? []}
          onChange={(option: SelectFieldOption_Values | undefined) => {

            mutateValueIntoRef(dirtRef, "feedcurve_id", Number(option?.id), animal.animal_id);
            if (isNaN(Number(option?.id))) {
              inputRef.current?.setValue('')
              dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, feedcurve_id: Number(option?.id), milestone_day: undefined } }))
              return;
            }

            const feedCurveMilestones = selectMilestoneOptionsByFeedCurve(store.getState(), [Number(option?.id)]) ?? undefined;
            if (feedCurveMilestones === undefined) {
              dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, feedcurve_id: Number(option?.id), milestone_day: undefined } }))
              inputRef.current?.setValue('')
              return;
            }

            const milestone = feedCurveMilestones?.find((milestone) => milestone.milestoneId === animal.milestone?.milestone_id);
            if (milestone !== undefined) {
              dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, feedcurve_id: Number(option?.id), feedcurve_milestones: milestone.id } }))
              mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", milestone.id, animal.animal_id);
              if (animal.milestone_day) {
                mutateValueIntoRef(dirtRef, "milestone_day", Math.floor(animal.milestone_day / 1000), animal.animal_id)
              }
              return;
            }

            const newMilestone = [...feedCurveMilestones].sort((milestoneA, milestoneB) => milestoneA.index - milestoneB.index)?.at(0);
            dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, feedcurve_id: Number(option?.id), feedcurve_milestones: newMilestone?.id, milestone: newMilestone ? { milestone_id: newMilestone.milestoneId, name: newMilestone.label } : newMilestone, milestone_day: Date.now() } }))
            mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", newMilestone ? newMilestone.id : NaN, animal.animal_id);
            mutateValueIntoRef(dirtRef, "milestone_day", Math.floor(Date.now() / 1000), animal.animal_id);
          }}
          valueOverrides={{
            defaultValue: selectedFeedCurveOption ?? undefined
          }}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greySecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
        />
      }
    />

    <NewLineInfoDisplay
      key={`milestone_${animal.animal_id}`}
      focusView={true}
      header={t("feed.feedCorrection.milestone", { defaultValue: "Milestone" })}
      enhancer={<glyphs.Milestone size="25px" />}
      content={animal.milestone?.name ?? ""}
      noContent={(animal.milestone === undefined || animal.milestone === null) ? t("negativeParams.milestone", { defaultValue: "No Milestone" }) : undefined}
      editMode={editMode}
      editContent={
        <NewSelect
          options={milestoneOptionsFromFeedcurve ?? []}
          onChange={(option: SelectFieldOption_Values | undefined) => {

            mutateValueIntoRef(dirtRef, "fk_feedcurve_milestones", Number(option?.id), animal.animal_id);
            mutateValueIntoRef(dirtRef,
              "milestone_day",
              Math.round((Date.now()) / 1000),
              animal.animal_id);
            dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, feedcurve_milestones: Number(option?.id), milestone_day: Date.now() } }))

          }}
          valueOverrides={{
            defaultValue: milestoneOptionsFromFeedcurve?.find(({ id }) => animal && animal.feedcurve_milestones === id) ?? undefined
          }}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greySecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
        />
      }
    />

    <NewLineInfoDisplay
      key={`milestoneDay_${animal.animal_id}`}
      focusView={true}
      header={t("params.milestoneDay", { defaultValue: "Milestone Day" })}
      enhancer={<glyphs.MilestoneDay size="25px" />}
      content={animal.milestone_day ? Math.floor((Date.now() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : undefined}
      noContent={(animal.milestone_day === undefined || animal.milestone_day === null) ? t("negativeParams.milestoneDay", { defaultValue: "No Milestone Day" }) : undefined}
      editMode={editMode}
      editContent={
        <NewInput
          isClearable
          ref={inputRef}
          onBlur={() => {
            const animal = dirtRef.current.find((animal) => animal.animal_id === animal.animal_id);
            if (animal && animal.milestone_day && animal.milestone_day !== animal.milestone_day) {
              dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, responder_number: animal.responder_number ?? undefined, milestone_day: animal.milestone_day * 1000 } }))
            }
          }}
          onChange={(e: string | number) => {
            if (e === "" || e === undefined || e === null) {
              mutateValueIntoRef(dirtRef, "milestone_day", NaN, animal.animal_id);
              return;
            }

            mutateValueIntoRef(
              dirtRef,
              "milestone_day",
              Math.round((Date.now() - Number(e) * 86400000) / 1000),
              animal.animal_id
            );
          }}
          valueOverrides={{
            defaultValue: animal.milestone_day ? Math.floor((Date.now() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : undefined,
            valueType: "number"
          }}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greySecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
        />
      }
    />

  </>)
})
// #endregion

// #region | Feed Correction
function mutateValueIntoRef(dirtRef: MutableRefObject<MaintainAnimal[]>, key: keyof MaintainAnimal, value: boolean | number, animalId: number) {
  if (dirtRef.current === undefined) {
    dirtRef.current = [];
  }

  const animalInRef = dirtRef.current.find((animal) => animal.animal_id === animalId);
  if (animalInRef) {
    Object.assign(animalInRef, { [key]: value });
  }
  dirtRef.current.push({ animal_id: animalId, [key]: value })
}

const debounceAnimalUpdate = debounce((key: keyof Animal, value: number, animal: Animal, dispatch: Dispatch<UnknownAction>) => {
  dispatch(updateActiveAnimal({ id: animal.animal_id, changes: { ...animal, [key]: value } }));
}, 500)

const FeedCorrection = memo(({ dirtRef, animal, editMode = false }: { dirtRef: MutableRefObject<MaintainAnimal[]>, animal: Animal, editMode: boolean }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [feed, setFeed] = useState<number | undefined>(animal?.correction);

  useEffect(() => {
    setFeed(animal?.correction)
  }, [animal?.correction]);

  const feedCorrection = animal?.correction === undefined || animal?.correction === null
    ? "0%"
    : `${animal?.correction}%`;

  return (
    <NewLineInfoDisplay
      key={`feedCorrection_${animal.animal_id}`}
      focusView={true}
      header={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })}
      enhancer={<glyphs.FeedCorrection size="25px" />}
      content={feedCorrection}
      noContent={feed === undefined ? t("negativeParams.feedCorrection", { defaultValue: "No Feed Corrections" }) : undefined}
      editMode={editMode}
      editContent={
        <NewInput
          isClearable
          onChange={(e) => { mutateValueIntoRef(dirtRef, "correction", Number(e), animal.animal_id); setFeed(Number(e)); debounceAnimalUpdate("correction", Number(e), animal, dispatch) }}
          valueOverrides={{
            defaultValue: feed,
            valueType: "number"
          }}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greySecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
        />
      }
    />
  )
})
// #endregion

// #region | Feed Skip
const FeedSkip = memo(({ locationId, editMode = false, feedSkipHandler }: { locationId: number, editMode: boolean, feedSkipHandler: FeedSkipHandler }) => {
  const { t } = useTranslation();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm });
    let connection = Graph_QL.create({ domain: farm });
    return { domain, connection };
  }, [farm]);

  const { call: getTerminals, data: terminals } = useTerminals(connection);

  useEffect(() => {
    getTerminals({})
  }, [connection, getTerminals])

  const { feedSkipTime, setFeedSkipTime } = feedSkipHandler;

  const terminalForPen = useMemo(() => { return terminals[domain]?.data?.find(terminal => terminal.fk_locations === locationId) }, [terminals, domain, locationId]);
  const skipTime = Number(terminalForPen?.skip_time ?? 0) * 1000;
  const isSkipped = skipTime > Date.now();
  const penSkip = isSkipped ? new LocaleFarmDate(skipTime).toLocaleFarmString() : "";
  const hoursLeft = isSkipped ? Number(((skipTime - Date.now()) / (1000 * 60 * 60)).toFixed(1)) : 0;
  const modifiedHoursLeft = feedSkipTime[locationId] !== undefined ? feedSkipTime[locationId] : hoursLeft;

  return (
    <NewLineInfoDisplay
      key={`feedSkip_${locationId}`}
      focusView={true}
      header={t("locationFeederPages.locationFeeder.feedSkip", { defaultValue: "Feed Skip" })}
      enhancer={<glyphs.FeedPlan size="25px" />}
      content={penSkip}
      noContent={modifiedHoursLeft === undefined || modifiedHoursLeft === 0 ? t("negativeParams.feedSkip", { defaultValue: "No Feed Skip" }) : undefined}
      editMode={editMode}
      editContent={
        <NewInput
          isClearable
          onChange={(e) => setFeedSkipTime(old => { return { ...old, [locationId]: Number(e) } })}
          valueOverrides={{
            defaultValue: modifiedHoursLeft,
            valueType: "number"
          }}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greySecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
        />
      }
    />
  )
})
// #endregion

// #region | Progress Circles
const ProgressCircles = memo(({ animalId }: { animalId: number }) => {
  const { t } = useTranslation();
  const { isLoading: loadingConsumption, entity: thisConsumption } = useAnimalConsumptionById(String(animalId));
  const animalConsumptions = thisConsumption?.consumptions;

  // Feed Consumption
  const consumptionInfo = useMemo(() => {
    if (animalConsumptions === undefined) return;

    const consumptionToday = animalConsumptions ? animalConsumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === 0) : undefined;
    const consumptionYesterday = animalConsumptions ? animalConsumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === -1) : undefined;

    return { consumptionToday, consumptionYesterday }
  }, [animalConsumptions])

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "start", height: "220px", flexShrink: "0", width: "100%" }}>
      {loadingConsumption && <LoadingSpinner />}
      {(!loadingConsumption && consumptionInfo !== undefined) && <>
        <MemoProgressCircle
          total={consumptionInfo?.consumptionToday?.total}
          progressed={consumptionInfo?.consumptionToday?.progressed}
          animation
          circleType={CircleTypes.feed}
          labels={{
            header: t("feed.general.remainingToday", { defaultValue: "remaining today" }),
            unit: <UNIT content={t("unit.kg", { defaultValue: "kg" })} color={colors.greyPrimary} glyph={<glyphs.Kg size={"15px"} fillColor={colors.greyPrimary} />} />
          }}
          extension={{ hasExtension: true }}
        />
        <MemoProgressCircle
          total={consumptionInfo?.consumptionYesterday?.total}
          progressed={consumptionInfo?.consumptionYesterday?.progressed}
          animation
          circleType={CircleTypes.feed}
          labels={{
            header: t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" }),
            unit: <UNIT content={t("unit.kg", { defaultValue: "kg" })} color={colors.greyPrimary} glyph={<glyphs.Kg size={"15px"} fillColor={colors.greyPrimary} />} />
          }}
          extension={{ hasExtension: true }}
        />
      </>}
    </div>
  )
})
// #endregion
